/* eslint-disable react/no-unknown-property */
import { types as t } from 'mobx-state-tree';
import { useCreateStore, useProvider, useStore } from 'mobx-store-provider';
import * as React from 'react';

interface VideoPlayOptions {
  subtitleEnabled?: boolean;
  pip?: boolean;
  controls?: boolean;
  light?: boolean;
  muted?: boolean;
  loop?: boolean;
  volume?: number;
  played?: number;
  ended?: number;
  loaded?: number;
  playbackRate?: number;
  loopTimes?: number;
  playedTime?: number;
}

export const ControlledVideoStore = t
  .model('DolEditorStore', {
    url: t.maybeNull(t.string),
    subtitleEnabled: t.optional(t.boolean, false),
    pip: t.optional(t.boolean, false),
    playing: t.optional(t.boolean, false),
    controls: t.optional(t.boolean, false),
    light: t.optional(t.boolean, false),
    muted: t.optional(t.boolean, false),
    loop: t.optional(t.boolean, false),
    volume: t.optional(t.number, 1),
    played: t.optional(t.number, 0),
    ended: t.optional(t.boolean, false),
    loaded: t.optional(t.number, 0),
    duration: t.optional(t.number, 0),
    playedSeconds: t.optional(t.number, 0),
    playbackRate: t.optional(t.number, 1),
    fullScreen: t.optional(t.boolean, false),
    loopTimes: t.optional(t.number, 2),
    playedTime: t.optional(t.number, 0),
  })
  .actions((self) => ({
    registerUrl(url, options: VideoPlayOptions = {}) {
      self.url = url;
      self.subtitleEnabled = options?.subtitleEnabled;
      self.pip = options?.pip;
      self.controls = options?.controls;
      self.light = options?.light;
      self.muted = options?.muted;
      self.loop = options?.loop;
      self.volume = options?.volume;
      self.played = options?.played;
      self.loaded = options?.loaded;
      self.playbackRate = options?.playbackRate;
    },
    setPlayed(played: number) {
      self.played = played;
    },
    setPlaybackRate(playbackRate: number) {
      self.playbackRate = playbackRate;
    },
    setPlayedSeconds(playedSeconds: number) {
      self.playedSeconds = playedSeconds;
    },
    setVolume(volume: number) {
      self.volume = volume;
    },
    setPlaying(playing: boolean) {
      self.playing = playing;
    },
    setEnded(ended: boolean) {
      self.ended = ended;
    },
    setDuration(duration) {
      self.duration = duration;
    },
    setMuted(muted: boolean) {
      self.muted = muted;
    },
    setFullScreen(fullScreen: boolean) {
      self.fullScreen = fullScreen;
    },
    reset() {
      self.playing = false;
      self.ended = false;
      self.playedSeconds = 0;
      self.played = 0;
      self.volume = 1;
      self.muted = false;
    },
  }))
  .actions((self) => ({
    playPause() {
      self.playing = !self.playing;
    },
    play() {
      self.playing = true;
      self.ended = false;
    },
    pause() {
      self.playing = false;
    },
    end() {
      self.playing = false;
      self.ended = true;
    },
    toggleSubtitle() {
      self.subtitleEnabled = !self.subtitleEnabled;
    },
    toggleLoop() {
      self.loop = !self.loop;
    },
    setLoopTimes() {
      // case 999999 is forever
      if (self.loopTimes === 999999) {
        self.loopTimes = 0;
      } else if (self.loopTimes === 2) {
        self.loopTimes = 999999;
      } else {
        self.loopTimes = self.loopTimes + 1;
      }
      self.playedTime = 0;
    },
    setPlayedTime(times) {
      self.playedTime = times;
    },
  }))
  .actions((self) => ({
    replay() {
      self.setPlayedSeconds(0);
      self.playing = true;
    },
  }));

export const useControlledVideoStore = () => {
  return useStore(ControlledVideoStore);
};

export const withControlledVideo = (Component: any) => (props: any) => {
  const controlledVideoCreateStore = useCreateStore(ControlledVideoStore, {});
  const ControlledVideoProvider = useProvider(ControlledVideoStore);

  return (
    <ControlledVideoProvider value={controlledVideoCreateStore}>
      <Component {...props} />
    </ControlledVideoProvider>
  );
};
